import { useNavigate } from "@tanstack/react-router";
import { createContext, lazy, Suspense, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { toast } from "@/components/ui/sonner";
import { useEntitiesMeta, useEntityMeta } from "./api";
import { recordTarget, targetHref } from "./links";

interface QuickCreateContextValue {
  openCreate: (entity: string) => void;
}

const QuickCreateContext = createContext<QuickCreateContextValue | null>(null);

/** Global "New <entity>" sheet for the TopBar and command palette. */
// The form (and its validation libraries) loads the first time someone uses "+ New", not with every page.
const EntityForm = lazy(() => import("./EntityForm").then((m) => ({ default: m.EntityForm })));

export function QuickCreateProvider({ children }: { children: ReactNode }) {
  const [entity, setEntity] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const openCreate = useCallback((name: string) => {
    setEntity(name);
    setOpen(true);
  }, []);
  const value = useMemo(() => ({ openCreate }), [openCreate]);

  return (
    <QuickCreateContext.Provider value={value}>
      {children}
      {entity && (
        <Suspense fallback={null}>
          <QuickCreateSheet entity={entity} open={open} onOpenChange={setOpen} />
        </Suspense>
      )}
    </QuickCreateContext.Provider>
  );
}

function QuickCreateSheet({ entity, open, onOpenChange }: { entity: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const meta = useEntityMeta(entity);
  const { data: entities } = useEntitiesMeta();
  const navigate = useNavigate();

  useEffect(() => {
    if (meta.error && open) {
      toast.error("Couldn't open the form", { description: meta.error.message });
      onOpenChange(false);
    }
  }, [meta.error, open, onOpenChange]);

  if (!meta.data) return null;
  return (
    <EntityForm
      open={open}
      onOpenChange={onOpenChange}
      meta={meta.data}
      mode="create"
      onSaved={(item) => {
        const summary = entities?.find((e) => e.name === entity);
        void navigate({ to: targetHref(recordTarget(summary, entity, item.id)) });
      }}
    />
  );
}

export function useQuickCreate(): QuickCreateContextValue {
  const ctx = useContext(QuickCreateContext);
  return ctx ?? { openCreate: () => toast("Quick create isn't available here") };
}
